
import { TypedVue } from "@/store/types";
import { Component, Inject, Prop, Watch } from "vue-property-decorator";
import VueI18n from "vue-i18n";
import { CompetencyStatus } from "@/lib/competencies";
import { CandidateSummary, FLAG_TOOLTIP_KEYS, TraitGroupDef } from "./shared";
import LevelDot from "./LevelDot.vue";
import DiscussionFlag from "./DiscussionFlag.vue";
import ParticipantTag from "./ParticipantTag.vue";

interface Tally {
  hp: number;
  p: number;
  lp: number;
  flags: number;
}

interface SummaryRowDef {
  key: keyof Tally;
  status: CompetencyStatus | null;
  label: string;
}

@Component({
  components: {
    LevelDot,
    DiscussionFlag,
    ParticipantTag,
  },
})
export default class ComparisonGrid extends TypedVue {
  @Inject("$i18n") readonly $i18n!: VueI18n;

  @Prop({ required: true })
  readonly candidates!: CandidateSummary[];

  @Prop({ required: true })
  readonly groups!: TraitGroupDef[];

  public order: string[] = [];
  public draggingId: string | null = null;
  public dragOverId: string | null = null;

  // Keeps user-chosen column order across filter toggles: existing ids keep their
  // relative order, re-activated/new ids append at the end. Only candidate columns
  // are reorderable; managers/team are pinned ahead of them (see orderedCandidates).
  @Watch("candidates", { immediate: true })
  onCandidatesChanged(): void {
    const ids = this.candidates.filter((c) => c.isCandidate).map((c) => c.id);
    this.order = [...this.order.filter((id) => ids.includes(id)), ...ids.filter((id) => !this.order.includes(id))];
  }

  // Managers and team members, in the order they were passed in (managers first).
  get pinnedParticipants(): CandidateSummary[] {
    return this.candidates.filter((c) => !c.isCandidate);
  }

  isLastPinned(participant: CandidateSummary): boolean {
    const pinned = this.pinnedParticipants;
    return pinned.length > 0 && pinned[pinned.length - 1].id === participant.id;
  }

  // Same wording as PrioritizationLegend.vue; flag row has status null.
  get summaryRows(): SummaryRowDef[] {
    return [
      { key: "hp", status: "highly-prioritized", label: "Highly prioritized" },
      { key: "p", status: "prioritized", label: "Prioritized" },
      { key: "lp", status: "neutral", label: "Less prioritized" },
      { key: "flags", status: null, label: "For discussion" },
    ];
  }

  // Pinned managers/team first, then the user-orderable candidate columns.
  get orderedCandidates(): CandidateSummary[] {
    const ordered = this.order.map((id) => this.candidates.find((c) => c.id === id)).filter((c): c is CandidateSummary => !!c);
    return [...this.pinnedParticipants, ...ordered];
  }

  isReorderable(id: string): boolean {
    return this.order.includes(id);
  }

  get tallyById(): Record<string, Tally> {
    const out: Record<string, Tally> = {};
    this.candidates.forEach((candidate) => {
      const tally: Tally = { hp: 0, p: 0, lp: 0, flags: 0 };
      this.groups.forEach((group) => {
        group.traits.forEach((trait) => {
          const { status, watchOut } = this.info(candidate, trait);
          if (status === "highly-prioritized") tally.hp++;
          else if (status === "prioritized") tally.p++;
          else tally.lp++;
          if (watchOut) tally.flags++;
        });
      });
      out[candidate.id] = tally;
    });
    return out;
  }

  info(candidate: CandidateSummary, trait: string): { status: CompetencyStatus; watchOut: boolean } {
    return candidate.traitInfo[trait] ?? { status: "neutral", watchOut: false };
  }

  flagTooltip(level: number): string {
    const key = FLAG_TOOLTIP_KEYS[level];
    return key ? this.$t(key).toString() : "";
  }

  onDragStart(id: string, event: DragEvent): void {
    if (!this.isReorderable(id)) {
      event.preventDefault();
      return;
    }
    this.draggingId = id;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
    }
  }

  onDragOver(id: string, event: DragEvent): void {
    // Pinned columns are never drop targets.
    this.dragOverId = id === this.draggingId || !this.isReorderable(id) ? null : id;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "move";
    }
  }

  // Remove from the source index and insert at the target's original index, so a
  // column can move one step right and can take the last position.
  onDrop(targetId: string): void {
    if (this.draggingId && this.draggingId !== targetId && this.isReorderable(targetId)) {
      const from = this.order.indexOf(this.draggingId);
      const to = this.order.indexOf(targetId);
      const next = [...this.order];
      next.splice(from, 1);
      next.splice(to, 0, this.draggingId);
      this.order = next;
    }
    this.onDragEnd();
  }

  onDragEnd(): void {
    this.draggingId = null;
    this.dragOverId = null;
  }
}
